<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">medical_services</span>
      <p>
        Formulario clínico completo de la atención médica. Registra el motivo,
        cuadro clínico, constantes vitales, diagnósticos, tratamiento y genera
        los documentos de la consulta.
      </p>
    </div>

    <!-- Datos generales -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">article</span>
        Datos generales
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in datosGenerales" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Dictado por voz -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">mic</span>
        Dictado por voz
      </h3>
      <div class="help-voz">
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color: orangered; font-size:28px;">mic_off</span>
          <p class="help-card-desc">Micrófono <strong>inactivo</strong>. Pulsa para iniciar el dictado en el campo correspondiente.</p>
        </div>
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color: green; font-size:28px;">mic</span>
          <p class="help-card-desc">Micrófono <strong>activo</strong>. El sistema está transcribiendo tu voz. Pulsa para detener.</p>
        </div>
      </div>
      <p class="help-card-desc" style="padding: 0 0.25rem;">
        Disponible en los campos: <strong>Descripción del Motivo</strong>, <strong>Cuadro Clínico</strong>
        y <strong>Texto del Certificado Médico</strong>. Solo funciona con la atención en estado PENDIENTE o PROCESO.
      </p>
    </section>

    <!-- Pestañas clínicas -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">tab</span>
        Pestañas clínicas
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="tab in tabs" :key="tab.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ tab.icon }}</span>
          <div>
            <p class="help-card-title">{{ tab.titulo }}</p>
            <p class="help-card-desc">{{ tab.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Acciones del panel lateral -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones del panel lateral
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Estados -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">info</span>
        Estados de la atención
      </h3>
      <div class="help-estados">
        <span
          v-for="e in estados"
          :key="e.label"
          class="help-estado-badge"
          :style="{ background: e.color, color: e.text }"
        >{{ e.label }}</span>
      </div>
      <p class="help-card-desc" style="padding: 0 0.25rem; margin-top: 0.25rem;">
        Los campos editables y los botones <strong>Actualizar</strong> y <strong>Finalizar</strong>
        solo están habilitados cuando la atención está en estado <strong>PENDIENTE</strong> o <strong>PROCESO</strong>.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Los campos <em>Descripción del Motivo</em> y <em>Cuadro Clínico</em> se guardan
        automáticamente al salir del campo. Para finalizar la atención es obligatorio
        tener al menos un diagnóstico registrado.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaAtencionForm",
  data() {
    return {
      datosGenerales: [
        {
          icon: "category",
          titulo: "Tipo de atención",
          desc:   "Selecciona 'Inicial' para la primera consulta del paciente o 'Subsecuente' para consultas de seguimiento.",
        },
        {
          icon: "text_snippet",
          titulo: "Descripción del motivo",
          desc:   "Describe con detalle el motivo por el que el paciente acude a la consulta. Máximo 250 caracteres. Se guarda automáticamente al salir del campo.",
        },
        {
          icon: "monitor_heart",
          titulo: "Cuadro clínico",
          desc:   "Registra la enfermedad o problema actual del paciente: síntomas, tiempo de evolución y características relevantes. Máximo 4000 caracteres. Se guarda automáticamente al salir del campo.",
        },
        {
          icon: "expand_more",
          titulo: "Más información",
          desc:   "Despliega campos adicionales: texto del certificado médico, motivo de finalización sin diagnóstico definitivo, y fechas de registro y actualización.",
        },
        {
          icon: "update",
          titulo: "Actualizar",
          desc:   "Guarda los cambios realizados en la atención y la mantiene en estado PROCESO para continuar editándola.",
        },
        {
          icon: "task",
          titulo: "Finalizar",
          desc:   "Cierra la atención y la pasa a estado FINALIZADA. Requiere al menos un diagnóstico registrado. Esta acción solicita confirmación y no se puede deshacer.",
        },
        {
          icon: "pregnancy",
          titulo: "Atención obstétrica",
          desc:   "Visible solo cuando el motivo de la cita es obstétrico. Abre el formulario especializado de atención obstétrica.",
        },
      ],
      tabs: [
        { icon: "monitor_heart",     titulo: "Constantes Vitales",    desc: "Registra presión arterial, temperatura, frecuencia cardíaca, peso, talla y otros signos vitales del paciente." },
        { icon: "history_edu",       titulo: "Antecedentes",          desc: "Documenta antecedentes personales, familiares, quirúrgicos, alergias y hábitos relevantes del paciente." },
        { icon: "accessibility_new", titulo: "Órganos y Sistemas",    desc: "Revisión por sistemas: estado de cada aparato y sistema del organismo según lo referido por el paciente." },
        { icon: "stethoscope",       titulo: "Examen Físico",         desc: "Hallazgos del examen físico realizado por el médico durante la consulta." },
        { icon: "diagnosis",         titulo: "Diagnósticos",          desc: "Registro de diagnósticos con códigos CIE-10, clasificados como presuntivos o definitivos. Es obligatorio para finalizar la atención." },
        { icon: "medication",        titulo: "Plan de Tratamiento",   desc: "Prescripción de medicamentos, dosis, vía de administración, frecuencia y duración del tratamiento." },
        { icon: "list_alt",          titulo: "Detalle",               desc: "Servicios y recursos utilizados: procedimientos realizados, exámenes solicitados, medicamentos e insumos empleados." },
        { icon: "dentistry",         titulo: "Odontograma",           desc: "Registro gráfico del estado de la dentición del paciente para atenciones odontológicas." },
        { icon: "visibility",        titulo: "Oftalmología",          desc: "Registro de agudeza visual, refracción y hallazgos oftalmológicos para atenciones de la especialidad." },
        { icon: "hearing",           titulo: "Otorrinolaringología",  desc: "Registro de hallazgos auditivos y de vías aéreas superiores para atenciones de la especialidad." },
      ],
      acciones: [
        { icon: "groups",           color: "#0059cc", titulo: "Interconsultas",   desc: "Gestiona solicitudes de interconsulta a otras especialidades médicas para esta atención." },
        { icon: "groups",           color: "#0059cc", titulo: "Ref./Der.",        desc: "Registra referencias o derivaciones del paciente a otra unidad de salud o especialista." },
        { icon: "allergies",        color: "#e05c00", titulo: "Alergias",         desc: "Consulta el historial de alergias registradas para el paciente." },
        { icon: "vaccines",         color: "#198754", titulo: "Vacunas",          desc: "Revisa el esquema de vacunación del paciente." },
        { icon: "clinical_notes",   color: "#6f42c1", titulo: "Ver Atención",     desc: "Muestra un resumen clínico completo de la atención con todas las notas registradas." },
        { icon: "clinical_notes",   color: "#6f42c1", titulo: "Consentimiento",   desc: "Genera y registra el consentimiento informado del paciente para la atención." },
        { icon: "picture_as_pdf",   color: "#dc3545", titulo: "Indicaciones",     desc: "Genera y descarga en PDF las indicaciones médicas emitidas en esta consulta." },
        { icon: "picture_as_pdf",   color: "#dc3545", titulo: "Certificado",      desc: "Genera y descarga en PDF el certificado médico. El texto se edita en el campo 'Texto del Certificado Médico' dentro de 'Más información'." },
        { icon: "attach_email",     color: "#fd7e14", titulo: "Enviar (pdf)",     desc: "Envía por correo electrónico los documentos PDF (indicaciones y certificado) al paciente." },
        { icon: "calendar_month",   color: "#198754", titulo: "Agendar Cita",     desc: "Redirige al asistente de citas para programar una nueva cita de seguimiento para este paciente." },
      ],
      estados: [
        { label: "PENDIENTE",         color: "#fff3cd", text: "#856404" },
        { label: "PROCESO",           color: "#cce5ff", text: "#004085" },
        { label: "FINALIZADA",        color: "#d4edda", text: "#155724" },
        { label: "PLANILLA GENERADA", color: "#e2d9f3", text: "#4a235a" },
        { label: "FACTURADA",         color: "#d1ecf1", text: "#0c5460" },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>